import React, { useState, useEffect, useRef } from "react";
import { API_BASE, apiFetch } from "./api";
import {
  Heart,
  Phone,
  Mail,
  MapPin,
  Clock,
  Camera,
  Home,
  Coffee,
  Instagram,
  ExternalLink,
  Play,
} from "lucide-react";

// TikTok markasının lucide-react'te ikonu olmadığı için kendi SVG'mizi kullanıyoruz.
const TikTok = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z" />
  </svg>
);

const TIKTOK_URL = "https://www.tiktok.com/@sahibimtatilde";

// Ziyaretçi yorum/değerlendirme formu. Gönderilince yorum "pending" olarak
// sunucuya yazılır; admin onayladıktan sonra sitede görünür.
const CommentModal = ({ onClose, onSubmitted }) => {
  const [form, setForm] = useState({
    name: "",
    location: "",
    pet: "",
    rating: 5,
    comment: "",
    website: "", // honeypot — bot doldurursa kayıt yapılmaz
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");
    try {
      await apiFetch("/api/comments", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setStatus("done");
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setStatus("error");
      setError(err.message || "Bir hata oluştu");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800">Değerlendirme Yap</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        {status === "done" ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">🐾</div>
            <p className="text-lg text-gray-700 mb-6">
              Yorumunuz alındı! Onaylandıktan sonra sitede yayınlanacaktır.
              Teşekkürler. ❤️
            </p>
            <button
              onClick={onClose}
              className="bg-gradient-to-r from-sage-500 to-sage-600 text-white px-6 py-3 rounded-full"
            >
              Kapat
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adınız *
              </label>
              <input
                type="text"
                required
                maxLength={80}
                value={form.name}
                onChange={set("name")}
                className="w-full border border-sage-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-400"
                placeholder="Örn: Ayşe D."
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bölge
                </label>
                <input
                  type="text"
                  maxLength={120}
                  value={form.location}
                  onChange={set("location")}
                  className="w-full border border-sage-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-400"
                  placeholder="Örn: Çukurova, Adana"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dostunuz
                </label>
                <input
                  type="text"
                  maxLength={120}
                  value={form.pet}
                  onChange={set("pet")}
                  className="w-full border border-sage-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-400"
                  placeholder="Örn: Pamuk (Kedi)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Puan
              </label>
              <div className="flex gap-1 text-3xl">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    className={
                      n <= form.rating ? "text-yellow-400" : "text-gray-300"
                    }
                    aria-label={`${n} yıldız`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yorumunuz *
              </label>
              <textarea
                required
                maxLength={1000}
                rows={4}
                value={form.comment}
                onChange={set("comment")}
                className="w-full border border-sage-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none"
                placeholder="Deneyiminizi paylaşın..."
              />
            </div>

            {/* Honeypot: gerçek kullanıcı görmez */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
              className="hidden"
              aria-hidden="true"
            />

            {status === "error" && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full bg-gradient-to-r from-sage-500 to-sage-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-lg transition-all disabled:opacity-60"
            >
              {status === "sending" ? "Gönderiliyor..." : "Gönder"}
            </button>
            <p className="text-xs text-gray-400 text-center">
              Yorumunuz onaylandıktan sonra yayınlanır.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

// Dikey Instagram video kartı: webde üzerine gelince oynar, mobilde dokununca.
// Sağ üstteki ikon gönderiyi yeni sekmede açar.
const ReelCard = ({ reel }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const playVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play()
      .then(() => setIsPlaying(true))
      .catch(() => {});
  };

  const pauseVideo = (reset = false) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    if (reset) v.currentTime = 0;
    setIsPlaying(false);
  };

  const togglePlay = () => (isPlaying ? pauseVideo() : playVideo());

  // Kart ekrandan çıkınca (kaydırınca) oynayan videoyu durdur
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) pauseVideo(true);
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="group relative aspect-[9/16] rounded-2xl md:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl bg-sage-100 cursor-pointer transform-gpu [will-change:transform] [-webkit-mask-image:-webkit-radial-gradient(white,black)] transition-all duration-300 hover:-translate-y-1"
      onMouseEnter={playVideo}
      onMouseLeave={() => pauseVideo(true)}
      onClick={togglePlay}
    >
      {reel.videoUrl ? (
        <video
          ref={videoRef}
          src={reel.videoUrl}
          poster={reel.poster || undefined}
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover rounded-2xl md:rounded-3xl"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-sage-200 to-sage-400 text-white text-4xl">
          🎬
        </div>
      )}

      {/* Okunabilirlik için degrade */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 pointer-events-none" />

      {/* Gönderiyi aç (sağ üst) */}
      <a
        href={reel.postUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label="Instagram'da aç"
        className="absolute top-2 right-2 md:top-3 md:right-3 z-10 bg-white/85 hover:bg-white text-sage-700 p-1.5 md:p-2 rounded-full shadow-md backdrop-blur-sm transition-all duration-300 hover:scale-110"
      >
        <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4" />
      </a>

      {/* Oynat ikonu (durduğunda görünür) */}
      <div
        className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${
          isPlaying ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="bg-white/30 backdrop-blur-sm rounded-full p-3 md:p-4 group-hover:scale-110 transition-transform duration-300">
          <Play className="w-5 h-5 md:w-7 md:h-7 text-white fill-white" />
        </div>
      </div>

      {/* Alt başlık */}
      {reel.caption ? (
        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3 pointer-events-none">
          <div className="flex items-center gap-1.5 text-white">
            <Instagram className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
            <p className="text-[11px] md:text-sm font-medium leading-tight line-clamp-2">
              {reel.caption}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Mobil için tek kartlık, iki yöne sonsuz (döngüsel) kaydırılabilen carousel.
// İçerik 3 kez çoğaltılır; kullanıcı uca yaklaşınca konum görünmez şekilde
// ortadaki kopyaya geri taşınır, böylece sağa-sola sınırsız kaydırılır.
const MobileReelCarousel = ({ reels }) => {
  const scrollRef = useRef(null);
  const loop = [...reels, ...reels, ...reels];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const setWidth = () => el.scrollWidth / 3;

    // Ortadaki kopyaya konumlan (iki yöne de kaydırılabilsin)
    el.scrollLeft = setWidth();

    let timer;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const w = setWidth();
        if (el.scrollLeft < w * 0.5) {
          el.scrollLeft += w;
        } else if (el.scrollLeft > w * 1.5) {
          el.scrollLeft -= w;
        }
      }, 80);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", onScroll);
    };
  }, [reels.length]);

  return (
    <div
      ref={scrollRef}
      className="no-scrollbar flex gap-3 overflow-x-auto snap-x snap-mandatory px-[11vw]"
    >
      {loop.map((reel, index) => (
        <div key={`${reel.id}-${index}`} className="w-[78vw] shrink-0 snap-center">
          <ReelCard reel={reel} />
        </div>
      ))}
    </div>
  );
};

const PetCareWebsite = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState("home");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [instagramReels, setInstagramReels] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [commentOpen, setCommentOpen] = useState(false);

  // Placeholder images for slider
  const sliderImages = [
    {
      id: 1,
      alt: "Sevimli kedi bakımı",
      caption: "Kedi dostlarımızla keyifli anlar",
    },
    {
      id: 2,
      alt: "Köpek oyun zamanı",
      caption: "Köpeklerle eğlenceli aktiviteler",
    },
    { id: 3, alt: "Bahçe bakımı", caption: "Bahçede güvenli oyun zamanı" },
    { id: 4, alt: "Yemek zamanı", caption: "Besleme ve bakım hizmetleri" },
    {
      id: 5,
      alt: "Sevgi dolu anlar",
      caption: "Her hayvanla özel bağ kuruyorum",
    },
  ];

  // Tarih biçimi: API'den ISO gelen tarihi "14 Mayıs 2026" formatına çevir.
  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d)) return "";
    return d.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Instagram videoları ve yorumlar artık sunucudaki API'den (Postgres) yüklenir.
  // İçerik eklemek için: videolar admin panelinden import edilir, yorumlar
  // ziyaretçi formuyla gelir ve admin panelinden onaylanır.
  useEffect(() => {
    // Göreli medya yollarını ("/videos/x.mp4") API origin'ine bağla.
    const withMedia = (u) =>
      u && u.startsWith("/") ? `${API_BASE}${u}` : u || "";

    apiFetch("/api/reels")
      .then((data) =>
        setInstagramReels(
          Array.isArray(data)
            ? data.map((r) => ({
                ...r,
                videoUrl: withMedia(r.videoUrl),
                poster: withMedia(r.poster),
              }))
            : []
        )
      )
      .catch(() => setInstagramReels([]));

    apiFetch("/api/comments")
      .then((data) => setTestimonials(Array.isArray(data) ? data : []))
      .catch(() => setTestimonials([]));
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);

    // Intersection Observer for active sections
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach((section) => observer.observe(section));

    // Auto slide for image carousel
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
      clearInterval(slideInterval);
    };
  }, [sliderImages.length]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-sage-50 relative overflow-x-hidden">
      {/* Background Pattern - WhatsApp style */}
      <div
        className="fixed inset-0 opacity-[0.2] pointer-events-none z-0"
        style={{
          backgroundImage: "url('/pattern.png')",
          backgroundSize: "300px",
          backgroundRepeat: "repeat",
        }}
      />

      {/* Main content with higher z-index */}
      <div className="relative z-10">
        {/* Navigation */}
        <nav
          className={`fixed top-0 w-full z-50 transition-all duration-500 ${
            scrollY > 50
              ? "bg-white backdrop-blur-md shadow-lg"
              : "bg-white md:bg-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <img
                  className="w-16 px-2"
                  src="/logo512.png"
                  alt="Sahibim Tatilde"
                ></img>
                <div className="flex flex-col leading-tight">
                  <span className="text-xl font-bold text-gray-800">
                    Sahibim Tatilde
                  </span>
                  <span className="text-xs md:text-sm text-sage-600 font-medium">
                    Tatil size, patiler bizde 🐾
                  </span>
                </div>
              </div>
              <div className="hidden md:flex space-x-8">
                {[
                  "home",
                  "hizmetler",
                  "hakkimda",
                  "referanslar",
                  "videolar",
                  "iletisim",
                ].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className={`capitalize transition-colors duration-300 ${
                      activeSection === section
                        ? "text-sage-600 font-semibold"
                        : "text-gray-600 hover:text-sage-500"
                    }`}
                  >
                    {section === "home"
                      ? "Ana Sayfa"
                      : section === "hizmetler"
                      ? "Hizmetler"
                      : section === "referanslar"
                      ? "Referanslar"
                      : section === "hakkimda"
                      ? "Hakkımda"
                      : section === "videolar"
                      ? "Videolar"
                      : "İletişim"}
                  </button>
                ))}
                <a
                  href="https://instagram.com/sahibimtatilde"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-sage-500 transition"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok'ta aç"
                  className="hover:text-sage-500 transition"
                >
                  <TikTok className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section
          id="home"
          className="min-h-screen flex items-center justify-center relative overflow-hidden pt-28 sm:pt-16 md:pt-0 pb-10"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-sage-100/50 to-sage-100/50"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-6">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-sage-500 to-sage-600">
                    Adana'da Sevgi Dolu
                  </span>
                  <br />
                  Kedi & Köpek Bakımı
                </h1>
                <p className="text-xl text-gray-600 mb-4 leading-relaxed">
                  Tatile çıktığınızda veya işiniz yoğun olduğunda, sevimli
                  dostlarınız güvende! Adana'da evde kedi bakıcılığı, köpek
                  bakıcılığı, köpek gezdirme ve günlük ziyaret hizmetleri.
                </p>
                <p className="text-lg text-sage-600 font-semibold mb-8">
                  Tatil sizde, patiler bizde! 🐾
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-center justify-center lg:justify-start">
                  <a
                    href="https://ig.me/m/sahibimtatilde"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-sage-500 to-sage-600 text-white px-7 py-3.5 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Mesaj At
                  </a>
                  <button
                    onClick={() => scrollToSection("hizmetler")}
                    className="border-2 border-sage-300 text-sage-600 px-7 py-3.5 rounded-full hover:bg-cream-50 transition-all duration-300"
                  >
                    Hizmetleri Gör
                  </button>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.instagram.com/sahibimtatilde/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram'da takip et"
                      title="Instagram'da takip et"
                      className="bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)] text-white p-3.5 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href={TIKTOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="TikTok'ta takip et"
                      title="TikTok'ta takip et"
                      className="bg-gray-900 text-white p-3.5 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      <TikTok className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-sage-200 to-sage-200 rounded-3xl p-8 shadow-2xl">
                  <div className="bg-white rounded-2xl p-6 text-center">
                    <img
                      src="/hero.png"
                      alt="Güvenilir Bakım - Sevimli dostlarınız için"
                      className="w-full h-64 object-cover rounded-xl mb-4"
                    />
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      Güvenilir Bakım
                    </h3>
                    <p className="text-gray-600">Sevimli dostlarınız için</p>
                  </div>
                </div>
                <div className="absolute -top-4 -right-1 md:-right-4 bg-yellow-400 rounded-full p-3 md:p-4 animate-bounce">
                  <span className="text-white text-xl md:text-2xl">🐱</span>
                </div>
                <div className="absolute -bottom-4 -left-2 md:-left-4 bg-blue-400 rounded-full p-3 md:p-4 animate-pulse">
                  <span className="text-white text-xl md:text-2xl">🐶</span>
                </div>
              </div>
            </div>
          </div>

          {/* Static floating elements (keeping original ones too) */}
          <div className="absolute top-20 left-2 md:left-10 opacity-60">
            <div className="bg-sage-300 rounded-full p-2 md:p-3 animate-float">
              <span className="text-white text-lg md:text-xl">🐾</span>
            </div>
          </div>
          <div className="absolute bottom-20 right-2 md:right-10 opacity-60">
            <div className="bg-sage-300 rounded-full p-2 md:p-3 animate-float-delayed">
              <span className="text-white text-lg md:text-xl">🐕</span>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="hizmetler" className="py-20 bg-white/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Hizmetlerimiz
              </h2>
              <p className="text-xl text-gray-600">
                Adana'da kedi ve köpekleriniz için sunduğum evde bakım
                hizmetleri
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-cream-50 to-cream-100 p-8 rounded-3xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="bg-sage-500 rounded-full p-4 w-fit mb-6">
                  <Home className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Evde Kedi & Köpek Bakımı
                </h3>
                <p className="text-gray-600 mb-6">
                  Evinizde kedi ve köpeklerinizin günlük bakım ihtiyaçlarını
                  karşılıyorum. Mama verme, su değişimi, kum temizliği ve sevgi
                  dolu ilgi.
                </p>
                <div className="flex items-center text-sage-500">
                  <Clock className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Saatlik hizmet</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-sage-50 to-cream-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="bg-sage-600 rounded-full p-4 w-fit mb-6">
                  <Coffee className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Köpek Gezdirme & Oyun
                </h3>
                <p className="text-gray-600 mb-6">
                  Köpek gezdirme, bahçede veya evde oyun oynama, egzersiz
                  yaptırma ve sosyalleşme aktiviteleri ile dostlarınızı mutlu
                  ediyorum.
                </p>
                <div className="flex items-center text-sage-600">
                  <Heart className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Sevgi dolu bakım</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-cream-100 to-cream-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="bg-sage-600 rounded-full p-4 w-fit mb-6">
                  <Camera className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Fotoğraf Paylaşımı
                </h3>
                <p className="text-gray-600 mb-6">
                  Sizin uzaktayken dostlarınızın fotoğraflarını çekip
                  paylaşıyorum. Böylece onların iyi olduğunu görebilirsiniz.
                </p>
                <div className="flex items-center text-sage-600">
                  <Camera className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Anı paylaşımı</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section
          id="hakkimda"
          className="py-20 bg-gradient-to-br from-cream-50/60 to-sage-50/60"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-800 mb-6">
                  Hakkımda
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Merhaba! Ben Beyza, 22 yaşındayım ve 2023'te Mersin
                  Üniversitesi Laboratuvar ve Veteriner Sağlık bölümünden mezun
                  oldum. Klinik, petshop, pet kuaför ve özel bakım deneyimlerim
                  sayesinde patili dostlarımızla bire bir ilgilenmenin ne kadar
                  değerli olduğunu öğrendim.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Hayvan koruma görevlisi olarak deneyim sahibi olduğum klinik
                  ve pet otellerinde gözlemlediğim bakımsız ve stresli ortamlar,
                  bana onların yanında güvenle ve sevgiyle ilgilenmenin önemini
                  gösterdi. Aldığım eğitimlerle her türlü patili dostumuzla
                  doğru şekilde iletişim kurabiliyorum.
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Patili dostlarımız benimle oyun oynar, gezdirilir ve keyifli
                  vakit geçirir, tüyleri taranır ve kişisel bakımları yapılır,
                  mamaları ve suları düzenli olarak verilir, sevgi dolu bir
                  ortamda vakit geçirir. Adana'nın her yerine hizmet sağlıyorum
                  ve bire bir ilgilenme sayesinde tatilinizde veya özel
                  günlerinizde hem patili dostlarımız mutlu olur hem de siz
                  huzurlu olabilirsiniz.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="bg-white rounded-full px-4 py-2 shadow-md">
                    <span className="text-sage-600 font-semibold">
                      🐱 Kedi Uzmanı
                    </span>
                  </div>
                  <div className="bg-white rounded-full px-4 py-2 shadow-md">
                    <span className="text-sage-700 font-semibold">
                      🐶 Köpek Dostu
                    </span>
                  </div>
                  <div className="bg-white rounded-full px-4 py-2 shadow-md">
                    <span className="text-sage-700 font-semibold">
                      ❤️ Güvenilir
                    </span>
                  </div>
                  <div className="bg-white rounded-full px-4 py-2 shadow-md">
                    <span className="text-blue-600 font-semibold">
                      🐾 Deneyimli
                    </span>
                  </div>
                </div>
              </div>
              <div className="relative">
                {/* Profile Photo Placeholder */}
                <div className="bg-gradient-to-br from-sage-200 to-sage-200 rounded-3xl p-4 shadow-2xl mb-6">
                  <div className="bg-white rounded-2xl p-6 text-center">
                    <div className="w-72 h-72 mx-auto rounded-full overflow-hidden mb-4 border-4 border-white shadow-lg">
                      <img
                        src="/hakkimda.png"
                        alt="Beyza - Hayvan Bakım Uzmanı"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      Veteriner Teknikeri ve
                      <br />
                      Hayvan Bakım Uzmanı
                    </h3>
                    <p className="text-gray-600">Sevgi ve deneyimle</p>
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -top-4 right-0 md:-right-4 bg-yellow-400 rounded-full p-2 md:p-3">
                  <span className="text-white text-lg md:text-xl">🌟</span>
                </div>
                <div className="absolute -bottom-4 -left-3 bg-green-400 rounded-full p-3">
                  <span className="text-white text-xl">🐾</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="referanslar" className="py-20 bg-white/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Mutlu Müşterilerimiz
              </h2>
              <p className="text-xl text-gray-600">
                Sevimli dostlarına emanet eden ailelerden gelen yorumlar
              </p>
              <div className="flex justify-center items-center mt-6 space-x-2">
                <div className="flex text-yellow-400 text-xl">
                  {"★".repeat(5)}
                </div>
                <span className="text-gray-600 ml-2">
                  4.9/5 Ortalama Puan (24 Değerlendirme)
                </span>
              </div>
            </div>

            {testimonials.length === 0 && (
              <p className="text-center text-gray-500 mb-8">
                Henüz yorum yok. İlk değerlendirmeyi siz yapın! 🐾
              </p>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-gradient-to-br from-cream-50 to-sage-50 p-6 rounded-3xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-sage-100"
                >
                  {/* Rating */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex text-yellow-400 text-lg">
                      {"★".repeat(testimonial.rating)}
                    </div>
                    <span className="text-sm text-gray-500">
                      {formatDate(testimonial.date)}
                    </span>
                  </div>

                  {/* Comment */}
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.comment}"
                  </p>

                  {/* Customer Info */}
                  <div className="border-t border-sage-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-gray-800">
                          {testimonial.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {testimonial.location}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="bg-gradient-to-r from-sage-500 to-sage-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {testimonial.pet}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Decorative paw */}
                  <div className="absolute top-2 right-2 opacity-20">
                    <span className="text-sage-400 text-2xl">🐾</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div class="mt-16 bg-gradient-to-r from-sage-100 to-sage-100 rounded-3xl p-8">
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-16">
                <div class="flex items-center space-x-4">
                  <div class="bg-sage-500 rounded-full p-3">
                    <span class="text-white text-xl">🏆</span>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold text-gray-800">24+</h3>
                    <p class="text-gray-600">Mutlu Müşteri</p>
                  </div>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="bg-sage-600 rounded-full p-3">
                    <span class="text-white text-xl">🐾</span>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold text-gray-800">50+</h3>
                    <p class="text-gray-600">Bakılan Hayvan</p>
                  </div>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="bg-sage-600 rounded-full p-3">
                    <span class="text-white text-xl">⭐</span>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold text-gray-800">4.9/5</h3>
                    <p class="text-gray-600">Ortalama Puan</p>
                  </div>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="bg-sage-600 rounded-full p-3">
                    <span class="text-white text-xl">❤️</span>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold text-gray-800">%100</h3>
                    <p class="text-gray-600">Tavsiye Oranı</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <p className="text-lg text-gray-600 mb-6">
                Siz de sevimli dostunuzu güvende bırakmak ister misiniz?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => scrollToSection("iletisim")}
                  className="bg-gradient-to-r from-sage-500 to-sage-600 text-white px-8 py-4 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Hemen İletişime Geçin
                </button>
                <button
                  onClick={() => setCommentOpen(true)}
                  className="bg-white text-sage-700 border-2 border-sage-400 px-8 py-4 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Heart className="w-5 h-5" />
                  Değerlendirme Yap
                </button>
              </div>
            </div>
          </div>
        </section>

        {commentOpen && (
          <CommentModal
            onClose={() => setCommentOpen(false)}
            onSubmitted={() =>
              apiFetch("/api/comments")
                .then((data) =>
                  setTestimonials(Array.isArray(data) ? data : [])
                )
                .catch(() => {})
            }
          />
        )}

        {/* Photo Gallery Slider */}
        <section className="py-20 bg-gradient-to-r from-sage-100 via-sage-100 to-sage-100 hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Mutlu Anılarımız
              </h2>
              <p className="text-xl text-gray-600">
                Sevimli dostlarımızla geçirdiğimiz keyifli zamanlardan kareler
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              {/* Main slider container */}
              <div className="relative h-96 overflow-hidden rounded-3xl shadow-2xl">
                {sliderImages.map((image, index) => (
                  <div
                    key={image.id}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${
                      index === currentSlide
                        ? "translate-x-0 opacity-100"
                        : index < currentSlide
                        ? "-translate-x-full opacity-0"
                        : "translate-x-full opacity-0"
                    }`}
                  >
                    <div className="w-full h-full bg-gradient-to-br from-sage-200 to-sage-200 flex items-center justify-center relative">
                      {/* Placeholder image content */}
                      <div className="text-center">
                        <div className="text-8xl mb-4">
                          {index === 0
                            ? "🐱"
                            : index === 1
                            ? "🐶"
                            : index === 2
                            ? "🌳"
                            : index === 3
                            ? "🍽️"
                            : "❤️"}
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {image.caption}
                        </h3>
                        <p className="text-white/90">
                          Placeholder - Gerçek fotoğraf buraya gelecek
                        </p>
                      </div>

                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                      {/* Caption */}
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {image.caption}
                        </h3>
                        <p className="text-white/80 text-sm">
                          Fotoğraf {index + 1} / {sliderImages.length}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation arrows */}
              <button
                onClick={() =>
                  setCurrentSlide(
                    (prev) =>
                      (prev - 1 + sliderImages.length) % sliderImages.length
                  )
                }
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <button
                onClick={() =>
                  setCurrentSlide((prev) => (prev + 1) % sliderImages.length)
                }
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Dots indicator */}
              <div className="flex justify-center space-x-3 mt-8">
                {sliderImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide
                        ? "bg-sage-500 w-8"
                        : "bg-white/50 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Instagram Videos Section */}
        <section id="videolar" className="py-20 bg-white/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 bg-sage-100 text-sage-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <Instagram className="w-4 h-4" />
                @sahibimtatilde
              </div>
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Patili Dostlarımızdan Kareler
              </h2>
              <p className="text-xl text-gray-600">
                Bakımını üstlendiğimiz minik dostlarımızın keyifli anları.
                Videonun üzerine gelin ya da dokunun ▶️
              </p>
            </div>

            {/* Mobil: tek kart, iki yöne sonsuz kaydırma */}
            <div className="md:hidden">
              <MobileReelCarousel reels={instagramReels} />
            </div>

            {/* Masaüstü: 3 yan yana (3'ten fazlaysa sonsuz kayan şerit) */}
            <div className="hidden md:block">
              {instagramReels.length > 3 ? (
                // 3'ten fazla video varsa: sonsuz, yavaşça kayan şerit
                // (üzerine gelince durur, böylece videoyu oynatabilirsin)
                <div className="group/marquee relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
                  <div className="flex w-max gap-6 animate-marquee-right group-hover/marquee:[animation-play-state:paused]">
                    {[...instagramReels, ...instagramReels].map((reel, index) => (
                      <div
                        key={`${reel.id}-${index}`}
                        className="w-56 shrink-0"
                      >
                        <ReelCard reel={reel} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {instagramReels.map((reel) => (
                    <ReelCard key={reel.id} reel={reel} />
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 md:mt-12">
              <a
                href="https://www.instagram.com/sahibimtatilde/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)] text-white px-8 py-4 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                <Instagram className="w-5 h-5" />
                Instagram'da İzle
              </a>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                <TikTok className="w-5 h-5" />
                TikTok'ta İzle
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          id="sss"
          className="py-20 bg-gradient-to-br from-cream-50/60 to-sage-50/60"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Sıkça Sorulan Sorular
              </h2>
              <p className="text-xl text-gray-600">
                Adana'da evde hayvan bakımı hakkında merak edilenler
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  🐱 Adana'da kedi bakıcısı hizmeti nasıl çalışıyor?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Tatildeyken veya yoğun günlerinizde evinize gelerek kedinizin
                  mamasını veriyor, suyunu tazeliyor, kumunu temizliyor ve
                  onunla oyun oynuyorum. Her ziyarette fotoğraf ve video
                  göndererek sizi bilgilendiriyorum.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  📍 Adana'nın hangi semtlerine hizmet veriyorsunuz?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Seyhan, Çukurova, Yüreğir, Sarıçam başta olmak üzere
                  Adana'nın her yerine evde kedi ve köpek bakım hizmeti
                  sağlıyorum.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  🏠 Evde hayvan bakımı pet otelinden neden daha iyi?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Kediler ve köpekler kendi evlerinde, alıştıkları ortamda çok
                  daha az stres yaşar. Evde bakımda hayvanınız rutinini korur,
                  hastalık bulaşma riski olmaz ve bire bir ilgi görür.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  🐕 Köpek gezdirme hizmeti veriyor musunuz?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Evet, Adana genelinde köpek gezdirme, oyun ve egzersiz
                  aktiviteleri sunuyorum. Enerjik köpekler için koşu ve uzun
                  yürüyüşler de yaptırıyorum.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  💰 Evde ücretli hayvan bakımı fiyatları nasıl belirleniyor?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Fiyatlar ziyaret sıklığına, hayvan sayısına ve hizmet
                  süresine göre değişir. Güncel fiyat bilgisi için
                  iletisim@sahibimtatilde.com adresine mail atabilir veya
                  Instagram'dan @sahibimtatilde hesabına yazabilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="iletisim" className="py-20 bg-white/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                İletişim
              </h2>
              <p className="text-xl text-gray-600">
                Fiyat bilgisi ve rezervasyon için benimle iletişime geçin.
                <button
                  onClick={() => scrollToSection("referanslar")}
                  className="text-sage-600 hover:text-sage-800 underline ml-1"
                >
                  Müşteri yorumlarını
                </button>{" "}
                inceleyebilir,
                <button
                  onClick={() => scrollToSection("hakkimda")}
                  className="text-sage-600 hover:text-sage-800 underline ml-1"
                >
                  deneyimlerim hakkında
                </button>{" "}
                daha fazla bilgi alabilirsiniz.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gradient-to-br from-cream-50 to-cream-100 p-8 rounded-3xl">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Bana Ulaşın
                </h3>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="bg-sage-500 rounded-full p-3">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-600">İletişim</p>
                      <p className="text-lg font-semibold text-gray-800">
                        Fiyat için mail atabilirsiniz
                      </p>
                    </div>
                  </div>

                  {/* Instagram ekledim */}
                  <a
                    href="https://www.instagram.com/sahibimtatilde/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4"
                  >
                    <div className="bg-gradient-to-r from-sage-600 to-sage-800 rounded-full p-3">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-600">Instagram</p>
                      <p className="text-lg font-semibold text-gray-800">
                        @sahibimtatilde
                      </p>
                    </div>
                  </a>

                  {/* TikTok ekledim */}
                  <a
                    href={TIKTOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4"
                  >
                    <div className="bg-gradient-to-r from-sage-600 to-sage-800 rounded-full p-3">
                      <TikTok className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-600">TikTok</p>
                      <p className="text-lg font-semibold text-gray-800">
                        @sahibimtatilde
                      </p>
                    </div>
                  </a>

                  <div className="flex items-center space-x-4">
                    <div className="bg-sage-600 rounded-full p-3">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-600">E-posta</p>
                      <p className="text-lg font-semibold text-gray-800">
                        iletisim@sahibimtatilde.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="bg-sage-600 rounded-full p-3">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-600">Hizmet Alanı</p>
                      <p className="text-lg font-semibold text-gray-800">
                        Adana geneli
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-sage-50 to-cream-50 p-8 rounded-3xl">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Hizmet Saatleri
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-sage-200 pb-2">
                    <span className="text-gray-600">Pazartesi - Cuma</span>
                    <span className="font-semibold text-gray-800">
                      08:00 - 18:00
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-sage-200 pb-2">
                    <span className="text-gray-600">Cumartesi</span>
                    <span className="font-semibold text-gray-800">
                      10:00 - 18:00
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-sage-200 pb-2">
                    <span className="text-gray-600">Pazar</span>
                    <span className="font-semibold text-gray-800">
                      10:00 - 18:00
                    </span>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-white rounded-2xl shadow-md">
                  <p className="text-center text-gray-600 mb-4">
                    Acil durumlar için 7/24 ulaşabilirsiniz
                  </p>
                  <a
                    href="https://ig.me/m/sahibimtatilde"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-sage-600 to-sage-800 text-white py-3 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Instagram className="w-5 h-5" />
                    Instagram'dan Mesaj Atın
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center space-x-2 mb-8">
              <img
                className="w-16 px-2"
                src="/logo512.png"
                alt="Sahibim Tatilde"
              ></img>
              <span className="text-2xl font-bold">Sahibim Tatilde</span>
            </div>
            <p className="text-center text-gray-400 mb-8">
              Tatil sizde, patiler bizde! 🐾
              <br />
              Sevimli dostlarınız için güvenilir, sevgi dolu bakım hizmetleri
            </p>
            <div className="flex justify-center space-x-6 mb-8">
              <span className="text-2xl">🐱</span>
              <span className="text-2xl">🐶</span>
              <span className="text-2xl">🐾</span>
              <span className="text-2xl">❤️</span>
            </div>
            <p className="text-center text-gray-500">
              © 2026 Sahibim Tatilde. Adana'da kedi bakıcılığı, köpek
              bakıcılığı ve evde hayvan bakım hizmetleri — Seyhan, Çukurova,
              Yüreğir, Sarıçam ve tüm Adana.
            </p>
          </div>
        </footer>

        <style jsx>{`
          @keyframes float {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes float-delayed {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-15px);
            }
          }

          /* Snake-like Paw Movement Patterns */
          @keyframes paw-trail-1 {
            0% {
              transform: translate(10vw, 20vh);
            }
            20% {
              transform: translate(60vw, 30vh);
            }
            40% {
              transform: translate(80vw, 60vh);
            }
            60% {
              transform: translate(30vw, 80vh);
            }
            80% {
              transform: translate(5vw, 50vh);
            }
            100% {
              transform: translate(10vw, 20vh);
            }
          }

          @keyframes paw-trail-2 {
            0% {
              transform: translate(80vw, 80vh);
            }
            20% {
              transform: translate(40vw, 70vh);
            }
            40% {
              transform: translate(20vw, 40vh);
            }
            60% {
              transform: translate(70vw, 20vh);
            }
            80% {
              transform: translate(90vw, 50vh);
            }
            100% {
              transform: translate(80vw, 80vh);
            }
          }

          /* Trail Dots - Following the main paws with increasing delays */
          @keyframes trail-dot-1-1 {
            0% {
              transform: translate(10vw, 20vh);
            }
            20% {
              transform: translate(60vw, 30vh);
            }
            40% {
              transform: translate(80vw, 60vh);
            }
            60% {
              transform: translate(30vw, 80vh);
            }
            80% {
              transform: translate(5vw, 50vh);
            }
            100% {
              transform: translate(10vw, 20vh);
            }
          }

          @keyframes trail-dot-1-2 {
            0% {
              transform: translate(10vw, 20vh);
            }
            20% {
              transform: translate(60vw, 30vh);
            }
            40% {
              transform: translate(80vw, 60vh);
            }
            60% {
              transform: translate(30vw, 80vh);
            }
            80% {
              transform: translate(5vw, 50vh);
            }
            100% {
              transform: translate(10vw, 20vh);
            }
          }

          @keyframes trail-dot-1-3 {
            0% {
              transform: translate(10vw, 20vh);
            }
            20% {
              transform: translate(60vw, 30vh);
            }
            40% {
              transform: translate(80vw, 60vh);
            }
            60% {
              transform: translate(30vw, 80vh);
            }
            80% {
              transform: translate(5vw, 50vh);
            }
            100% {
              transform: translate(10vw, 20vh);
            }
          }

          @keyframes trail-dot-1-4 {
            0% {
              transform: translate(10vw, 20vh);
            }
            20% {
              transform: translate(60vw, 30vh);
            }
            40% {
              transform: translate(80vw, 60vh);
            }
            60% {
              transform: translate(30vw, 80vh);
            }
            80% {
              transform: translate(5vw, 50vh);
            }
            100% {
              transform: translate(10vw, 20vh);
            }
          }

          @keyframes trail-dot-1-5 {
            0% {
              transform: translate(10vw, 20vh);
            }
            20% {
              transform: translate(60vw, 30vh);
            }
            40% {
              transform: translate(80vw, 60vh);
            }
            60% {
              transform: translate(30vw, 80vh);
            }
            80% {
              transform: translate(5vw, 50vh);
            }
            100% {
              transform: translate(10vw, 20vh);
            }
          }

          /* Trail dots for Paw 2 */
          @keyframes trail-dot-2-1 {
            0% {
              transform: translate(80vw, 80vh);
            }
            20% {
              transform: translate(40vw, 70vh);
            }
            40% {
              transform: translate(20vw, 40vh);
            }
            60% {
              transform: translate(70vw, 20vh);
            }
            80% {
              transform: translate(90vw, 50vh);
            }
            100% {
              transform: translate(80vw, 80vh);
            }
          }

          @keyframes trail-dot-2-2 {
            0% {
              transform: translate(80vw, 80vh);
            }
            20% {
              transform: translate(40vw, 70vh);
            }
            40% {
              transform: translate(20vw, 40vh);
            }
            60% {
              transform: translate(70vw, 20vh);
            }
            80% {
              transform: translate(90vw, 50vh);
            }
            100% {
              transform: translate(80vw, 80vh);
            }
          }

          @keyframes trail-dot-2-3 {
            0% {
              transform: translate(80vw, 80vh);
            }
            20% {
              transform: translate(40vw, 70vh);
            }
            40% {
              transform: translate(20vw, 40vh);
            }
            60% {
              transform: translate(70vw, 20vh);
            }
            80% {
              transform: translate(90vw, 50vh);
            }
            100% {
              transform: translate(80vw, 80vh);
            }
          }

          @keyframes trail-dot-2-4 {
            0% {
              transform: translate(80vw, 80vh);
            }
            20% {
              transform: translate(40vw, 70vh);
            }
            40% {
              transform: translate(20vw, 40vh);
            }
            60% {
              transform: translate(70vw, 20vh);
            }
            80% {
              transform: translate(90vw, 50vh);
            }
            100% {
              transform: translate(80vw, 80vh);
            }
          }

          @keyframes trail-dot-2-5 {
            0% {
              transform: translate(80vw, 80vh);
            }
            20% {
              transform: translate(40vw, 70vh);
            }
            40% {
              transform: translate(20vw, 40vh);
            }
            60% {
              transform: translate(70vw, 20vh);
            }
            80% {
              transform: translate(90vw, 50vh);
            }
            100% {
              transform: translate(80vw, 80vh);
            }
          }

          /* Apply animations */
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }

          .animate-float-delayed {
            animation: float-delayed 4s ease-in-out infinite 1s;
          }

          /* Snake Trail Animations */
          .animate-paw-trail-1 {
            animation: paw-trail-1 20s linear infinite;
          }

          .animate-paw-trail-2 {
            animation: paw-trail-2 25s linear infinite 10s;
          }

          /* Trail Dots - Each dot follows with increasing delay */
          .animate-trail-dot-1-1 {
            animation: trail-dot-1-1 20s linear infinite 0.8s;
          }

          .animate-trail-dot-1-2 {
            animation: trail-dot-1-2 20s linear infinite 1.6s;
          }

          .animate-trail-dot-1-3 {
            animation: trail-dot-1-3 20s linear infinite 2.4s;
          }

          .animate-trail-dot-1-4 {
            animation: trail-dot-1-4 20s linear infinite 3.2s;
          }

          .animate-trail-dot-1-5 {
            animation: trail-dot-1-5 20s linear infinite 4s;
          }

          .animate-trail-dot-2-1 {
            animation: trail-dot-2-1 25s linear infinite 10.8s;
          }

          .animate-trail-dot-2-2 {
            animation: trail-dot-2-2 25s linear infinite 11.6s;
          }

          .animate-trail-dot-2-3 {
            animation: trail-dot-2-3 25s linear infinite 12.4s;
          }

          .animate-trail-dot-2-4 {
            animation: trail-dot-2-4 25s linear infinite 13.2s;
          }

          .animate-trail-dot-2-5 {
            animation: trail-dot-2-5 25s linear infinite 14s;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PetCareWebsite;
